/**
 * Palette de couleurs des catégories analytiques (chips colorés dans toute
 * l'app). Les 17 premières couleurs correspondent au backfill des catégories
 * de dépense par défaut (CAT01→CAT17) côté backend — l'ordre doit rester
 * aligné pour que la détection « couleur déjà utilisée » fonctionne.
 */
export interface PaletteColor {
  hex: string;
  name: string;
}

export const CATEGORY_PALETTE: PaletteColor[] = [
  { hex: '#EF4444', name: 'Rouge' },
  { hex: '#F97316', name: 'Orange' },
  { hex: '#F59E0B', name: 'Ambre' },
  { hex: '#EAB308', name: 'Jaune' },
  { hex: '#84CC16', name: 'Lime' },
  { hex: '#22C55E', name: 'Vert' },
  { hex: '#10B981', name: 'Émeraude' },
  { hex: '#14B8A6', name: 'Turquoise' },
  { hex: '#06B6D4', name: 'Cyan' },
  { hex: '#0EA5E9', name: 'Ciel' },
  { hex: '#3B82F6', name: 'Bleu' },
  { hex: '#6366F1', name: 'Indigo' },
  { hex: '#8B5CF6', name: 'Violet' },
  { hex: '#A855F7', name: 'Pourpre' },
  { hex: '#D946EF', name: 'Fuchsia' },
  { hex: '#EC4899', name: 'Rose' },
  { hex: '#F43F5E', name: 'Rose vif' },
  { hex: '#64748B', name: 'Ardoise' },
  { hex: '#78716C', name: 'Pierre' },
  { hex: '#0891B2', name: 'Sarcelle' },
];

/** Couleur neutre par défaut (dimension sans couleur explicite). */
export const DEFAULT_CATEGORY_COLOR = '#64748B';

/** Normalise une couleur hex en `#RRGGBB` majuscule, ou null si invalide. */
export function normalizeColor(c?: string | null): string | null {
  if (!c) return null;
  const s = c.trim().toUpperCase();
  return /^#[0-9A-F]{6}$/.test(s) ? s : null;
}

/**
 * Couleurs de la palette encore disponibles = non déjà utilisées par les
 * dimensions passées. `preserve` (couleur courante en édition) reste proposée.
 */
export function availablePaletteColors(
  usedColors: Array<string | null | undefined>,
  preserve?: string | null,
): PaletteColor[] {
  const used = new Set(
    usedColors.map((c) => normalizeColor(c)).filter(Boolean) as string[],
  );
  const keep = normalizeColor(preserve);
  return CATEGORY_PALETTE.filter(
    (p) => !used.has(p.hex) || (keep && p.hex === keep),
  );
}

/**
 * Choisit la 1ʳᵉ couleur libre de la palette pour une nouvelle catégorie,
 * afin de proposer d'emblée une couleur non utilisée.
 */
export function firstAvailableColor(
  usedColors: Array<string | null | undefined>,
): string {
  const avail = availablePaletteColors(usedColors);
  return (avail[0] ?? CATEGORY_PALETTE[0]).hex;
}
